/* eslint-disable camelcase */
import React, { useContext, useEffect } from 'react';
import { useFormik } from 'formik';
import { OnlineBookingContext } from '../..';
import BottomNav from '../BottomNav';
import Form from '../../../form-elements/Form';
import Legend from '../../../form-elements/Legend';
import Select from '../../../form-elements/Select';
import Required from '../../../form-elements/Required';
import RadioGroup from '../../../form-elements/RadioGroup';
import TextArea from '../../../form-elements/TextArea';
import TextField from '../../../form-elements/TextField';
import { singlePassengerInitialValues } from '../../utils';

const Passengers = ({
  savedPassengers,
  savedTravelDetails: { no_travellers },
}) => {
  const {
    form: {
      page: { passenger },
    },
    initialPassengerValues,
    passengerValidationSchema,
    disable,
  } = useContext(OnlineBookingContext);

  const {
    handleBlur,
    touched,
    values,
    errors,
    setFieldValue,
    setValues,
    setTouched,
  } = useFormik({
    initialValues: savedPassengers || initialPassengerValues,
    validationSchema: passengerValidationSchema,
    enableReinitialize: true,
  });

  const {
    lead_passenger: {
      title: leadPassengerTitle,
      fields: {
        title: leadTitleMeta,
        first_name: leadFirstNameMeta,
        last_name: leadLastNameMeta,
        date_of_birth: dateOfBirthMeta,
        sex: sexMeta,
        telephone: telephoneMeta,
        email_address: emailAddressMeta,
      },
    },
    additional_passenger: {
      fields: {
        title: titleMeta,
        first_name: firstNameMeta,
        last_name: lastNameMeta,
      },
    },
  } = passenger;

  const {
    lead_title,
    lead_first_name,
    lead_last_name,
    date_of_birth,
    sex,
    telephone,
    email,
    additional_passengers,
  } = values;

  useEffect(() => {
    if (!savedPassengers) {
      const newAdditionalPassengers = [];

      for (let index = 0; index < no_travellers; index++) {
        newAdditionalPassengers.push(singlePassengerInitialValues);
      }

      setFieldValue('additional_passengers', newAdditionalPassengers);
    }
  }, []);

  const updateAdditionalPassenger = (fieldName, value, index) => {
    const newAdditionalPassengers = [...additional_passengers];
    newAdditionalPassengers[index][fieldName] = value;

    setFieldValue('additional_passengers', additional_passengers);
  };

  return (
    <section className="c-form c-form--passengers">
      <div className="c-form__main">
        <Form className="c-form__body">
          <fieldset className="c-form__online-fieldset c-form__online-fieldset--lead-passenger">
            <Legend>{leadPassengerTitle}</Legend>

            <h1 className="c-form__online-fieldset__title u-color--purple">
              <span
                dangerouslySetInnerHTML={{ __html: leadPassengerTitle }}
              ></span>
              <span className="o-text">All fields are mandatory</span>
            </h1>

            <div className="c-form__item c-form__item--lead-title u-mb-9">
              <TextField
                label={
                  <>
                    {leadTitleMeta.label}
                    {leadTitleMeta.required && <Required />}
                  </>
                }
                name={leadTitleMeta.id}
                placeholder={leadTitleMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) => setFieldValue('lead_title', value)}
                value={lead_title}
                validationError={errors.lead_title && touched[leadTitleMeta.id]}
                validationMessage={errors.lead_title}
              />
            </div>

            <div className="c-form__item c-form__item--lead-f-name u-mb-9">
              <TextField
                label={
                  <>
                    {leadFirstNameMeta.label}
                    {leadFirstNameMeta.required && <Required />}
                  </>
                }
                name={leadFirstNameMeta.id}
                placeholder={leadFirstNameMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) =>
                  setFieldValue('lead_first_name', value)
                }
                value={lead_first_name}
                validationError={
                  errors.lead_first_name && touched[leadFirstNameMeta.id]
                }
                validationMessage={errors.lead_first_name}
              />
            </div>

            <div className="c-form__item c-form__item--lead-l-name u-mb-9">
              <TextField
                label={
                  <>
                    {leadLastNameMeta.label}
                    {leadLastNameMeta.required && <Required />}
                  </>
                }
                name={leadLastNameMeta.id}
                placeholder={leadLastNameMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) => setFieldValue('lead_last_name', value)}
                value={lead_last_name}
                validationError={
                  errors.lead_last_name && touched[leadLastNameMeta.id]
                }
                validationMessage={errors.lead_last_name}
              />
            </div>

            <div className="c-form__item c-form__item--lead-dob u-mb-9">
              <TextField
                label={
                  <>
                    {dateOfBirthMeta.label}
                    {dateOfBirthMeta.required && <Required />}
                  </>
                }
                name={dateOfBirthMeta.id}
                type="date"
                placeholder={dateOfBirthMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) => setFieldValue('date_of_birth', value)}
                value={date_of_birth}
                validationError={
                  errors.date_of_birth && touched[dateOfBirthMeta.id]
                }
                validationMessage={errors.date_of_birth}
              />
            </div>

            <div className="c-form__item--online-booking-radio c-form__item--lead-sex u-mb-9">
              <RadioGroup
                name={sexMeta.id}
                id={sexMeta.id}
                label={sexMeta.label}
                options={sexMeta.options}
                handleChange={(value) => setFieldValue('sex', value)}
                value={sex}
              />
            </div>

            <div className="c-form__item c-form__item--lead-telephone u-mb-9">
              <TextField
                label={
                  <>
                    {telephoneMeta.label}
                    {telephoneMeta.required && <Required />}
                  </>
                }
                name={telephoneMeta.id}
                placeholder={telephoneMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) => setFieldValue('telephone', value)}
                value={telephone}
                validationError={errors.telephone && touched[telephoneMeta.id]}
                validationMessage={errors.telephone}
              />
            </div>

            <div className="c-form__item c-form__item--lead-email u-mb-19">
              <TextField
                label={
                  <>
                    {emailAddressMeta.label}
                    {emailAddressMeta.required && <Required />}
                  </>
                }
                name={emailAddressMeta.id}
                placeholder={emailAddressMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) => setFieldValue('email', value)}
                value={email}
                validationError={errors.email && touched[emailAddressMeta.id]}
                validationMessage={errors.email}
              />
            </div>
          </fieldset>

          {additional_passengers.map((passenger, index) => (
            <fieldset className="c-form__online-fieldset c-form__online-fieldset--additional-passenger">
              <Legend>Passenger {index + 1} Info</Legend>

              <h1 className="c-form__online-fieldset__title u-color--purple">
                Passenger {index + 1} Info
              </h1>

              <div className="c-form__item c-form__item--lead-title u-mb-9">
                <TextField
                  label={
                    <>
                      {titleMeta.label}
                      {titleMeta.required && <Required />}
                    </>
                  }
                  name={titleMeta.id}
                  placeholder={titleMeta.placeholder}
                  onBlur={handleBlur}
                  handleChange={(value) =>
                    updateAdditionalPassenger('title', value, index)
                  }
                  value={additional_passengers[index].title}
                  // validationError={errors.additional_passengers[index].title && touched[titleMeta.id]}
                  // validationMessage={errors.additional_passengers[index].title}
                />
              </div>

              <div className="c-form__item c-form__item--lead-f-name u-mb-9">
                <TextField
                  label={
                    <>
                      {firstNameMeta.label}
                      {firstNameMeta.required && <Required />}
                    </>
                  }
                  name={firstNameMeta.id}
                  placeholder={firstNameMeta.placeholder}
                  onBlur={handleBlur}
                  handleChange={(value) =>
                    updateAdditionalPassenger('first_name', value, index)
                  }
                  value={additional_passengers[index].first_name}
                  // validationError={
                  //   errors.lead_first_name && touched[firstNameMeta.id]
                  // }
                  // validationMessage={errors.rist}
                />
              </div>

              <div className="c-form__item c-form__item--lead-l-name u-mb-9">
                <TextField
                  label={
                    <>
                      {lastNameMeta.label}
                      {lastNameMeta.required && <Required />}
                    </>
                  }
                  name={lastNameMeta.id}
                  placeholder={lastNameMeta.placeholder}
                  onBlur={handleBlur}
                  handleChange={(value) =>
                    updateAdditionalPassenger('last_name', value, index)
                  }
                  value={additional_passengers[index].last_name}
                  // validationError={
                  //   errors.last_name && touched[lastNameMeta.id]
                  // }
                  // validationMessage={errors.lead_last_name}
                />
              </div>
            </fieldset>
          ))}
        </Form>
      </div>

      <BottomNav
        canContinue={!disable(errors, values, initialPassengerValues)}
        values={values}
      />
    </section>
  );
};

export default Passengers;
